// The «رمز التفعيل» field (system/activation--desktop).
//
// erp-core POST /v1/erp/license/activate takes two different bodies
// (internal/license/module.go activateRequest):
//   - `{activation_code}` — the short code, `XXXX-XXXX-XXXX` (board shape),
//     12 symbols of the code alphabet (no I/O/0/1, read over the phone).
//     The node exchanges it for its signed file through a redemption
//     service; offered only when GET /license/status lists `code` in
//     `activation_methods`.
//   - `{file}` — the signed activation file (Ed25519 envelope
//     `{"payload":…,"signature":…}`) pasted as text. It is JSON, so it always
//     contains «{»; it is kept VERBATIM (base64 is case-sensitive — never
//     uppercase or hyphenate it). Offered when `activation_methods` lists
//     `file`.
// A short code sent under `file` is refused by the server as
// `license_malformed`, so the two are never mixed.
//
// The node's first-run code (POST /v1/erp/auth/bootstrap, printed to the
// erp-core log at boot) has the same `XXXX-XXXX-XXXX` shape: formatGroupedCode.

const CODE_GROUP = 4;
export const CODE_LENGTH = 12;

/** erp-core license.CodeAlphabet: 32 symbols, no I / O / 0 / 1. */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';

function foldDigits(s: string): string {
  return s.replace(/[٠-٩]/g, (d) => String(AR_DIGITS.indexOf(d)));
}

/** True when the text is a pasted activation file (JSON envelope), not a short code. */
export function isActivationFile(raw: string): boolean {
  return raw.includes('{');
}

/** The short code's characters only: A–Z and 0–9, uppercased, at most 12. */
export function codeChars(raw: string): string {
  return foldDigits(raw)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, CODE_LENGTH);
}

/**
 * What the field shows for what was typed: a file stays as pasted; a short
 * code becomes `XXXX-XXXX-XXXX` (partial groups as typed: `XK7P-4M`).
 */
export function formatActivationInput(raw: string): string {
  if (isActivationFile(raw)) return raw;
  return formatGroupedCode(raw);
}

/** A 12-symbol code as `XXXX-XXXX-XXXX` (partial groups as typed); never a file. */
export function formatGroupedCode(raw: string): string {
  const chars = codeChars(raw);
  const groups: string[] = [];
  for (let i = 0; i < chars.length; i += CODE_GROUP) groups.push(chars.slice(i, i + CODE_GROUP));
  return groups.join('-');
}

/** A complete short code: exactly 12 symbols, all from the code alphabet. */
export function isCompleteCode(value: string): boolean {
  const chars = codeChars(value);
  return chars.length === CODE_LENGTH && [...chars].every((c) => CODE_ALPHABET.includes(c));
}

/** A short code is complete at 12 valid symbols; a pasted file is judged by the server. */
export function isCompleteInput(value: string): boolean {
  if (isActivationFile(value)) return value.trim().length > 2;
  return isCompleteCode(value);
}

export type ActivationBody = { activation_code: string } | { file: string };

/** The POST body: a pasted file under `file`, a short code under `activation_code`. */
export function activationBody(value: string): ActivationBody {
  if (isActivationFile(value)) return { file: value.trim() };
  return { activation_code: formatActivationInput(value) };
}
